import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker, MapContext, Line, ZoomableGroup } from 'react-simple-maps';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import {
  getRiMapOrigins, getRiDriveDistribution, getRiStateTable, getRiParkMarker,
} from '../../../api/auth';
import { BUCKET_COLORS, BUCKET_ORDER } from '../lib/driveBucket';
import { decodePolyline } from '../lib/polyline';
import { normalizeUsState } from '../lib/stateRegion';
import DriveBucketBadge from '../primitives/DriveBucketBadge';
import { RiLoading, RiEmpty, RiError, RiSparseNotice, RiPagination } from '../primitives/States';
import { orFailed, didFail, useReload } from '../lib/loadState';
import OriginDetailPanel from '../panels/OriginDetailPanel';
import StateDetailPanel from '../panels/StateDetailPanel';
import useFocusTrap from '../../../hooks/useFocusTrap';
import { RI_TABLE_FRAME, RI_THEAD, RI_TH, RI_ROW, RI_ROW_SELECTED, RI_ROW_CLICKABLE, RI_TD, clickableRowProps } from '../lib/tableStyles';

const CLUSTER_ZOOM_THRESHOLD = 8;
const CLUSTER_CELL_DEG = 3;
const STATE_PAGE_SIZE = 25;

// Uniform 1-decimal float formatter for the Origins-by-State table so e.g.
// "19" doesn't render alongside "21.3"; nulls render as the standard "—".
const fmt1 = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(1));

// Mercator projects everything except the poles, but we still guard against
// null/NaN so a single bad row can't crash Marker's internal destructure.
function SafeMarker({ coordinates, ...rest }) {
  const ctx = useContext(MapContext);
  const projection = ctx && ctx.projection;
  if (!projection || !Array.isArray(coordinates)) return null;
  const projected = projection(coordinates);
  if (!projected || !Number.isFinite(projected[0]) || !Number.isFinite(projected[1])) return null;
  return <Marker coordinates={coordinates} {...rest} />;
}

// World atlas backs the projection so non-US origins can render in their
// real geographic position; us-atlas overlays state borders on top so the
// map keeps the familiar US-with-state-lines look.
const WORLD_GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const US_GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const MAX_POLYLINES = 500;
// Origins for the bubble map. Well above a large property's route count (the
// reference client has ~9,900), so the map shows every origin, not the first half.
export const ORIGINS_LIMIT = 20000;
// The polyline fetch carries ~35 KB of geometry per origin, so it stays capped;
// its rows are merged into the loaded origins rather than replacing them.
const POLYLINE_FETCH_LIMIT = 5000;

/**
 * Attach the polylines from a polyline fetch to the already-loaded origins,
 * matched by coordinates, keeping every origin (the polyline fetch is capped and
 * would otherwise shrink the map back to its cap).
 * @param {object[]} origins the loaded origins
 * @param {object[]} withPolylines rows from the polyline fetch
 * @returns {object[]}
 */
export const mergePolylines = (origins, withPolylines) => {
  const key = (o) => `${o.origin_lat},${o.origin_lon}`;
  const byKey = new Map((withPolylines || []).map((o) => [key(o), o.encoded_polyline]));
  return (origins || []).map((o) => (byKey.get(key(o)) ? { ...o, encoded_polyline: byKey.get(key(o)) } : o));
};
// Approximate geo span of the Mercator viewport at zoom=1 (scale 200) — used
// to cull polylines/markers that fall outside the current pan/zoom window.
const ALBERS_LON_SPAN = 230;
const ALBERS_LAT_SPAN = 130;

// react-simple-maps' SVG sets its viewBox once on mount and does not recompute
// when the container resizes (browser zoom, sidebar collapse, etc.). Observe
// the wrapper and bump a key to remount the map at the new dimensions.
function useContainerSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const r = entry.contentRect;
      setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

export default function MapOriginsTab({ clientId, range, updateParam, searchParams }) {
  const from = range?.from ?? null;
  const to = range?.to ?? null;
  const [origins, setOrigins] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [stateTable, setStateTable] = useState(null);
  const [park, setPark] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState({});
  const [reloadKey, retry] = useReload();
  const [bookedOnly, setBookedOnly] = useState(false);
  const [showRoutes, setShowRoutes] = useState(false);
  const [polylinesLoaded, setPolylinesLoaded] = useState(false);
  const [stateSort, setStateSort] = useState({ key: 'total_origins', dir: 'desc' });
  const [statePage, setStatePage] = useState(1);
  const [viewState, setViewState] = useState({ coordinates: [-96, 38], zoom: 4 });
  // react-simple-maps v3 treats ZoomableGroup center/zoom as initial values only;
  // bump this counter on button-driven changes to remount the map at the new view.
  // Mouse pan/zoom updates viewState via onMoveEnd without touching this counter.
  const [viewSeq, setViewSeq] = useState(0);
  const [confirmShow, setConfirmShow] = useState(null); // { count, resolve }
  const [expandedClusterKey, setExpandedClusterKey] = useState(null);
  const showRoutesBtnRef = useRef(null);
  // Bumped every time the origins are re-read (client, range or retry). A polyline fetch
  // started under an earlier value is for a superseded origin set and must not be applied.
  const originsEpochRef = useRef(0);
  const mapWrapperRef = useRef(null);
  const mapSize = useContainerSize(mapWrapperRef);

  // react-simple-maps' onMoveEnd passes { coordinates, zoom }; onMove only passes
  // screen-space { x, y, zoom }. We only update viewport state at move end so we
  // always have valid lat/lon coordinates for viewport culling.
  const handleMoveEnd = (pos) => {
    if (!pos || !Array.isArray(pos.coordinates)) return;
    setViewState({ coordinates: pos.coordinates, zoom: pos.zoom });
  };

  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 8;
  const ZOOM_STEP = 1.5;
  const zoomIn = () => {
    setViewState((s) => ({ ...s, zoom: Math.min((s.zoom || 1) * ZOOM_STEP, MAX_ZOOM) }));
    setViewSeq((n) => n + 1);
  };
  const zoomOut = () => {
    setViewState((s) => ({ ...s, zoom: Math.max((s.zoom || 1) / ZOOM_STEP, MIN_ZOOM) }));
    setViewSeq((n) => n + 1);
  };
  const zoomReset = () => {
    setViewState({ coordinates: [-96, 38], zoom: 4 });
    setViewSeq((n) => n + 1);
  };

  const bucketFilter = searchParams.get('bucket') || '';
  const stateFilter = searchParams.get('state') || '';
  const originParam = searchParams.get('origin') || '';
  // Per spec p.16, the Map tab supports "Filter by accuracy tier".
  // Stored as a comma-separated subset of {address, postal, weather_geo, ip_geo}.
  // Empty = show all tiers.
  const accuracyFilter = (searchParams.get('accuracy') || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const accuracySet = new Set(accuracyFilter);
  const toggleAccuracyTier = (tier) => {
    const next = new Set(accuracySet);
    if (next.has(tier)) next.delete(tier); else next.add(tier);
    updateParam('accuracy', next.size ? [...next].join(',') : '');
  };

  const stateFromLabel = (label) => {
    if (!label) return null;
    const parts = String(label).split(',').map((s) => s.trim());
    return normalizeUsState(parts[1]);
  };

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    // The refetched origins carry no polylines, so anything derived from the previous set
    // is stale: leaving `showRoutes` on would read "Hide Routes" over an empty map, and
    // `polylinesLoaded` would stop the next "Show Routes" from fetching for the new range.
    originsEpochRef.current += 1;
    // A "show N routes?" dialog still open belongs to the old origin set: close it, and
    // answer "no" so the toggle that is awaiting it unblocks.
    setConfirmShow((cur) => {
      if (cur) cur.resolve(false);
      return null;
    });
    setShowRoutes(false);
    setPolylinesLoaded(false);
    setPolylineDataAvailable(null);
    Promise.all([
      orFailed(getRiMapOrigins(clientId, { includePolylines: false, limit: ORIGINS_LIMIT, from, to })),
      orFailed(getRiDriveDistribution(clientId, { from, to })),
      orFailed(getRiStateTable(clientId, { from, to })),
      getRiParkMarker(clientId).catch(() => null),
    ]).then(([o, d, s, p]) => {
      if (cancelled) return;
      setFailed({ origins: didFail(o), distribution: didFail(d), stateTable: didFail(s) });
      setOrigins(didFail(o) ? [] : o?.data || []);
      setDistribution(didFail(d) ? [] : d?.data || []);
      setStateTable(didFail(s) ? [] : s?.data || []);
      setPark(p?.data || null);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [clientId, from, to, reloadKey]);

  const askConfirmShow = useCallback((count) => (
    new Promise((resolve) => setConfirmShow({ count, resolve }))
  ), []);

  const resolveConfirmShow = useCallback((result) => {
    setConfirmShow((cur) => {
      if (cur) cur.resolve(result);
      return null;
    });
    // Return focus to the Show/Hide Routes button after the modal unmounts.
    requestAnimationFrame(() => showRoutesBtnRef.current?.focus());
  }, []);

  // True once we've fetched with includePolylines AND found at least one row
  // that has a non-empty polyline. Drives the empty-state message + button
  // disabled-once-known-empty UX (per #24).
  const [polylineDataAvailable, setPolylineDataAvailable] = useState(null); // null = unknown

  /**
   * Fetches the polylines for the origins currently on screen and merges them in.
   *
   * @returns {Promise<'stale'|undefined>} `'stale'` when the origin set was replaced (range,
   *   client or retry) while the request or the confirm dialog was open — the result was
   *   discarded and the caller must not switch routes on.
   */
  const loadPolylines = async () => {
    if (polylinesLoaded || !clientId) return undefined;
    const epoch = originsEpochRef.current;
    const count = origins?.length || 0;
    if (count > MAX_POLYLINES) {
      const ok = await askConfirmShow(count);
      if (epoch !== originsEpochRef.current) return 'stale';
      if (!ok) return undefined;
    }
    const r = await getRiMapOrigins(clientId, { includePolylines: true, limit: POLYLINE_FETCH_LIMIT, from, to }).catch(() => null);
    if (epoch !== originsEpochRef.current) return 'stale';
    if (r?.data) {
      setOrigins((cur) => mergePolylines(cur, r.data));
      setPolylinesLoaded(true);
      const anyPolylines = r.data.some((o) => o.encoded_polyline);
      setPolylineDataAvailable(anyPolylines);
    } else {
      setPolylineDataAvailable(false);
    }
    return undefined;
  };

  const toggleShowRoutes = async () => {
    if (!showRoutes) {
      if (await loadPolylines() === 'stale') return;
      setShowRoutes(true);
    } else {
      setShowRoutes(false);
    }
  };

  const visibleOrigins = useMemo(() => {
    if (!origins) return [];
    return origins.filter((o) => {
      if (bookedOnly && !o.has_booking) return false;
      if (bucketFilter && o.drive_bucket !== bucketFilter) return false;
      if (stateFilter && stateFromLabel(o.origin_label) !== stateFilter) return false;
      if (accuracySet.size && !accuracySet.has(o.origin_accuracy || 'unknown')) return false;
      return true;
    });
  }, [origins, bookedOnly, bucketFilter, stateFilter, accuracyFilter.join(',')]);

  // geoAlbersUsa renders the 50 US states (with AK/HI insets) + PR. Anything
  // outside this rough bbox is silently dropped by SafeMarker; surface the
  // list in a callout under the map so users aren't misled by the US-only
  // projection. Bbox check is intentionally generous (includes territories).
  const nonUsOrigins = useMemo(() => {
    if (!visibleOrigins.length) return [];
    return visibleOrigins.filter((o) => {
      if (o.origin_lat == null || o.origin_lon == null) return false;
      const inUs = o.origin_lat >= 18 && o.origin_lat <= 72
        && o.origin_lon >= -180 && o.origin_lon <= -65;
      return !inUs;
    });
  }, [visibleOrigins]);

  const originsInViewport = useMemo(() => {
    if (!visibleOrigins.length) return [];
    if (!Array.isArray(viewState.coordinates) || viewState.coordinates.length < 2) return visibleOrigins;
    const [centerLon, centerLat] = viewState.coordinates;
    const halfLon = (ALBERS_LON_SPAN / viewState.zoom) / 2;
    const halfLat = (ALBERS_LAT_SPAN / viewState.zoom) / 2;
    const minLon = centerLon - halfLon;
    const maxLon = centerLon + halfLon;
    const minLat = centerLat - halfLat;
    const maxLat = centerLat + halfLat;
    return visibleOrigins.filter((o) =>
      o.origin_lon >= minLon && o.origin_lon <= maxLon
      && o.origin_lat >= minLat && o.origin_lat <= maxLat
    );
  }, [visibleOrigins, viewState]);

  const clustered = useMemo(() => {
    if (viewState.zoom > CLUSTER_ZOOM_THRESHOLD) return null;
    const cells = new Map();
    for (const o of originsInViewport) {
      if (o.origin_lat == null || o.origin_lon == null) continue;
      const key = `${Math.floor(o.origin_lat / CLUSTER_CELL_DEG)}_${Math.floor(o.origin_lon / CLUSTER_CELL_DEG)}`;
      if (!cells.has(key)) {
        cells.set(key, { key, count: 0, latSum: 0, lonSum: 0, buckets: {}, hasBooking: false, origins: [] });
      }
      const c = cells.get(key);
      c.count += 1;
      c.latSum += o.origin_lat;
      c.lonSum += o.origin_lon;
      c.buckets[o.drive_bucket] = (c.buckets[o.drive_bucket] || 0) + 1;
      c.origins.push(o);
      if (o.has_booking) c.hasBooking = true;
    }
    return [...cells.values()].map((c) => {
      const dominant = Object.entries(c.buckets).sort((a, b) => b[1] - a[1])[0][0];
      return {
        key: c.key,
        count: c.count,
        lat: c.latSum / c.count,
        lon: c.lonSum / c.count,
        bucket: dominant,
        hasBooking: c.hasBooking,
        origins: c.origins,
      };
    });
  }, [originsInViewport, viewState.zoom]);

  const decodedPolylines = useMemo(() => {
    if (!showRoutes) return [];
    const withPolylines = originsInViewport.filter((o) => o.encoded_polyline);
    const capped = withPolylines.slice(0, MAX_POLYLINES);
    return capped
      .map((o) => {
        const coords = decodePolyline(o.encoded_polyline).map(([lat, lon]) => [lon, lat]);
        return coords.length
          ? { coords, color: BUCKET_COLORS[o.drive_bucket] || BUCKET_COLORS.unknown }
          : null;
      })
      .filter(Boolean);
  }, [showRoutes, originsInViewport]);

  const polylineCapHit = showRoutes
    && originsInViewport.filter((o) => o.encoded_polyline).length > MAX_POLYLINES;

  // Origins passing the "global" filters that affect the whole card (booked-only
  // + state). The bucket filter is intentionally *not* applied here so the donut
  // keeps showing the full per-bucket distribution; the bucket filter only
  // highlights one slice and drives the center label below.
  const donutOrigins = useMemo(() => {
    if (!origins) return [];
    return origins.filter((o) => {
      if (bookedOnly && !o.has_booking) return false;
      if (stateFilter && stateFromLabel(o.origin_label) !== stateFilter) return false;
      if (accuracySet.size && !accuracySet.has(o.origin_accuracy || 'unknown')) return false;
      return true;
    });
  }, [origins, bookedOnly, stateFilter, accuracyFilter.join(',')]);

  // When any client-side filter is active, rebuild the per-bucket counts so the
  // donut, its center, and the legend below mirror the map. Without filters,
  // fall back to the server-aggregated distribution (which carries authoritative
  // avg_fuel numbers we'd otherwise have to recompute).
  const effectiveDistribution = useMemo(() => {
    const anyFilterActive = bookedOnly || stateFilter || accuracySet.size > 0;
    if (!anyFilterActive) return distribution || [];
    const counts = new Map();
    const fuelSum = new Map();
    const fuelN = new Map();
    for (const o of donutOrigins) {
      const b = o.drive_bucket || 'unknown';
      counts.set(b, (counts.get(b) || 0) + 1);
      if (o.fuel_gallons != null) {
        fuelSum.set(b, (fuelSum.get(b) || 0) + Number(o.fuel_gallons));
        fuelN.set(b, (fuelN.get(b) || 0) + 1);
      }
    }
    const total = [...counts.values()].reduce((s, n) => s + n, 0);
    return BUCKET_ORDER
      .filter((b) => counts.has(b))
      .map((b) => ({
        bucket: b,
        routes: counts.get(b),
        pct: total ? Math.round((counts.get(b) / total) * 1000) / 10 : 0,
        avg_fuel: fuelN.get(b) ? Math.round((fuelSum.get(b) / fuelN.get(b)) * 10) / 10 : null,
      }));
  }, [bookedOnly, stateFilter, donutOrigins, distribution, accuracyFilter.join(',')]);

  const ringData = useMemo(
    () => effectiveDistribution.map((d) => ({ name: d.bucket, value: d.routes, pct: d.pct })),
    [effectiveDistribution]
  );

  // Selecting a bucket (legend pill or clicking a slice) drops the center to
  // that bucket's count; otherwise it shows the total for whatever filter set
  // the donut currently represents.
  const ringTotal = useMemo(() => {
    if (bucketFilter) {
      const match = effectiveDistribution.find((d) => d.bucket === bucketFilter);
      return match ? match.routes : 0;
    }
    return effectiveDistribution.reduce((s, d) => s + d.routes, 0);
  }, [effectiveDistribution, bucketFilter]);

  // When any client-side filter is active (drive bucket pill OR accuracy tier),
  // re-aggregate state stats from the loaded origins so the table mirrors what's
  // on the map. Otherwise reuse the backend's aggregated stateTable.
  const filteredStateTable = useMemo(() => {
    const filterActive = !!bucketFilter || accuracySet.size > 0;
    if (!filterActive) return stateTable || [];
    if (!origins) return [];
    const byState = new Map();
    for (const o of origins) {
      if (bucketFilter && o.drive_bucket !== bucketFilter) continue;
      if (accuracySet.size && !accuracySet.has(o.origin_accuracy || 'unknown')) continue;
      if (bookedOnly && !o.has_booking) continue;
      const s = stateFromLabel(o.origin_label);
      if (!s) continue;
      if (!byState.has(s)) byState.set(s, []);
      byState.get(s).push(o);
    }
    const round1 = (n) => Math.round(n * 10) / 10;
    const out = [];
    // R10: mirror the backend — surface single-origin states tagged
    // "single_observation" instead of dropping them on the FE recompute.
    for (const [state_region, rows] of byState.entries()) {
      const hours = rows.map((r) => r.drive_time_hours).filter((v) => v != null);
      const miles = rows.map((r) => r.drive_distance_miles).filter((v) => v != null);
      const fuel = rows.map((r) => r.fuel_gallons).filter((v) => v != null);
      const congMins = rows
        .map((r) => (r.congestion_delay_seconds != null ? Math.max(0, Number(r.congestion_delay_seconds)) / 60 : null))
        .filter((v) => v != null);
      const sum = (a) => a.reduce((s, v) => s + Number(v), 0);
      out.push({
        state_region,
        total_origins: rows.length,
        avg_drive_hours: hours.length ? round1(sum(hours) / hours.length) : null,
        avg_miles: miles.length ? Math.round(sum(miles) / miles.length) : null,
        avg_fuel_gallons: fuel.length ? round1(sum(fuel) / fuel.length) : null,
        avg_congestion_min: congMins.length ? round1(sum(congMins) / congMins.length) : null,
        min_hours: hours.length ? round1(Math.min(...hours)) : null,
        max_hours: hours.length ? round1(Math.max(...hours)) : null,
        confidence: rows.length === 1 ? 'single_observation' : 'tracked',
      });
    }
    return out;
  }, [bucketFilter, bookedOnly, origins, stateTable, accuracyFilter.join(',')]);

  const sortedStates = useMemo(() => {
    const arr = filteredStateTable
      .map((r) => {
        const canonical = normalizeUsState(r.state_region);
        return canonical ? { ...r, state_region: canonical } : null;
      })
      .filter(Boolean);
    arr.sort((a, b) => {
      const av = a[stateSort.key];
      const bv = b[stateSort.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return stateSort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filteredStateTable, stateSort]);

  // Largest origin count in the filtered list — the inline Origins bars are scaled
  // to it, so a bar reads "share of the busiest state" on every page of the table.
  const maxStateOrigins = useMemo(
    () => sortedStates.reduce((m, r) => Math.max(m, Number(r.total_origins) || 0), 0),
    [sortedStates],
  );

  const stateTotalPages = Math.max(1, Math.ceil(sortedStates.length / STATE_PAGE_SIZE));
  const pagedStates = useMemo(() => {
    const start = (statePage - 1) * STATE_PAGE_SIZE;
    return sortedStates.slice(start, start + STATE_PAGE_SIZE);
  }, [sortedStates, statePage]);

  useEffect(() => { setStatePage(1); }, [stateSort.key, stateSort.dir, bucketFilter, bookedOnly, accuracyFilter.join(','), from, to]);

  const selectedStateRow = useMemo(() => {
    if (!stateFilter) return null;
    return sortedStates.find((r) => r.state_region === stateFilter) || null;
  }, [sortedStates, stateFilter]);

  const selectedStateOrigins = useMemo(() => {
    if (!stateFilter || !origins) return [];
    return origins.filter((o) => {
      if (stateFromLabel(o.origin_label) !== stateFilter) return false;
      if (bucketFilter && o.drive_bucket !== bucketFilter) return false;
      if (bookedOnly && !o.has_booking) return false;
      return true;
    });
  }, [origins, stateFilter, bucketFilter, bookedOnly]);

  if (loading) return <RiLoading height={300} />;

  const selectedOriginCoords = originParam
    ? originParam.split(',').map(Number)
    : null;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      {/* Hero map */}
      <div className="xl:col-span-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Guest Origins</h3>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <label className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200">
              <input type="checkbox" checked={bookedOnly} onChange={(e) => setBookedOnly(e.target.checked)} />
              Booked only
            </label>
            <button type="button" ref={showRoutesBtnRef} onClick={toggleShowRoutes}
              disabled={polylineDataAvailable === false}
              title={polylineDataAvailable === false ? 'Route geometry not yet available for this dataset' : undefined}
              className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900">
              {showRoutes ? 'Hide Routes' : 'Show Routes'}
            </button>
            {/* Per spec p.16, "Filter by accuracy tier" on the Map tab.
                Tiers: address (~10m), postal (~5–15km), weather_geo (inherited),
                ip_geo (~10–50km). Empty selection = all tiers shown. */}
            <div className="inline-flex items-center rounded border border-gray-200 dark:border-gray-700 overflow-hidden max-[399px]:flex max-[399px]:w-full" role="group" aria-label="Filter by origin accuracy tier">
              {[
                { key: 'address',     label: 'Address' },
                { key: 'postal',      label: 'Postal' },
                { key: 'weather_geo', label: 'Weather' },
                { key: 'ip_geo',      label: 'IP' },
              ].map(({ key, label }, i) => {
                const active = accuracySet.has(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleAccuracyTier(key)}
                    aria-pressed={active}
                    title={`Toggle ${label} accuracy tier`}
                    className={`px-2 py-1 max-[399px]:flex-1 ${i > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''} ${
                      active
                        ? 'bg-[#32B1FF] text-white'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {accuracyFilter.length > 0 && (
              <button type="button" onClick={() => updateParam('accuracy', '')}
                className="px-2 py-1 rounded bg-[#32B1FF]/10 text-[#32B1FF] hover:bg-[#32B1FF]/20">
                Clear tier ✕
              </button>
            )}
            {bucketFilter && (
              <button type="button" onClick={() => updateParam('bucket', '')}
                className="px-2 py-1 rounded bg-[#32B1FF]/10 text-[#32B1FF] hover:bg-[#32B1FF]/20">
                Clear: {bucketFilter} ✕
              </button>
            )}
            {stateFilter && (
              <button type="button" onClick={() => updateParam('state', '')}
                className="px-2 py-1 rounded bg-[#32B1FF]/10 text-[#32B1FF] hover:bg-[#32B1FF]/20">
                State: {stateFilter} ✕
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2 mb-3">Every guest origin pinned on the map, sized by drive-time bucket. Use the filters above to narrow by booking, tier, or state.</p>

        <div ref={mapWrapperRef} className="relative">
          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/70 dark:bg-gray-900/70 rounded-lg" role="status" aria-label="Loading map data">
              <div className="h-8 w-8 rounded-full border-2 border-[#32B1FF]/30 border-t-[#32B1FF] animate-spin" />
              <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">Loading guest origins…</p>
            </div>
          )}
          <ComposableMap key={`${mapSize.w}x${mapSize.h}-${viewSeq}`} projection="geoMercator" projectionConfig={{ scale: 200 }} className="w-full h-auto" style={{ maxHeight: 560 }}>
            <ZoomableGroup center={viewState.coordinates} zoom={viewState.zoom} minZoom={MIN_ZOOM} maxZoom={MAX_ZOOM} onMoveEnd={handleMoveEnd}>
              {/* World countries — soft background so non-US origins still land
                  on something recognizable. */}
              <Geographies geography={WORLD_GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography key={geo.rsmKey} geography={geo}
                      fill="rgba(229, 231, 235, 0.25)" stroke="rgba(255,255,255,0.5)" strokeWidth={0.4 / Math.max((viewState.zoom || 1) / 2, 1)}
                      style={{ default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } }}
                    />
                  ))
                }
              </Geographies>
              {/* US states — overlaid on top so the familiar state borders are
                  still front and center. */}
              <Geographies geography={US_GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography key={geo.rsmKey} geography={geo}
                      fill="rgba(229, 231, 235, 0.4)" stroke="#fff" strokeWidth={0.5 / Math.max((viewState.zoom || 1) / 2, 1)}
                      style={{ default: { outline: 'none' }, hover: { outline: 'none', fill: 'rgba(209, 213, 219, 0.6)' }, pressed: { outline: 'none' } }}
                    />
                  ))
                }
              </Geographies>

              {(() => {
                // Keep visual marker/stroke size at ≈raw × 2 at the default
                // zoom (3). Below zoom 2 the transform applies naturally so
                // markers shrink as you zoom out; above zoom 2 the divisor
                // cancels the transform so they don't balloon at deep zoom.
                const z = Math.max((viewState.zoom || 1) / 2, 1);
                return (
                  <>
                    {decodedPolylines.map((p, i) => (
                      <Line key={`poly-${i}`} coordinates={p.coords}
                        stroke={p.color}
                        strokeWidth={1.4 / z} strokeOpacity={0.65} fill="none" />
                    ))}

                    {clustered ? (
                      clustered.flatMap((c) => {
                        const isExpanded = expandedClusterKey === c.key;
                        if (isExpanded) {
                          const ringR = (Math.min(3 + Math.log2(c.count + 1) * 1.3, 10) + 6) / z;
                          return [
                            <SafeMarker key={`ring-${c.key}`} coordinates={[c.lon, c.lat]}
                              onClick={() => setExpandedClusterKey(null)}>
                              <circle r={ringR}
                                fill={BUCKET_COLORS[c.bucket] || BUCKET_COLORS.unknown}
                                fillOpacity={0.08}
                                stroke={BUCKET_COLORS[c.bucket] || BUCKET_COLORS.unknown}
                                strokeOpacity={0.4}
                                strokeDasharray="2 2"
                                strokeWidth={0.6 / z}
                                style={{ cursor: 'pointer' }} />
                            </SafeMarker>,
                            ...c.origins.map((o, j) => (
                              <SafeMarker key={`co-${c.key}-${j}`} coordinates={[o.origin_lon, o.origin_lat]}
                                onClick={(e) => {
                                  if (e?.stopPropagation) e.stopPropagation();
                                  updateParam('origin', `${o.origin_lat},${o.origin_lon}`);
                                }}>
                                <circle r={(o.has_booking ? 3.2 : 2) / z}
                                  fill={BUCKET_COLORS[o.drive_bucket] || BUCKET_COLORS.unknown}
                                  fillOpacity={o.has_booking ? 0.85 : 0.5}
                                  stroke={o.has_booking ? '#111827' : '#fff'}
                                  strokeWidth={0.5 / z}
                                  style={{ cursor: 'pointer' }} />
                              </SafeMarker>
                            )),
                          ];
                        }
                        if (c.count === 1) {
                          const only = c.origins[0];
                          return [
                            <SafeMarker key={`c-${c.key}`} coordinates={[c.lon, c.lat]}
                              onClick={() => updateParam('origin', `${only.origin_lat},${only.origin_lon}`)}>
                              <circle r={(only.has_booking ? 3.2 : 2) / z}
                                fill={BUCKET_COLORS[only.drive_bucket] || BUCKET_COLORS.unknown}
                                fillOpacity={only.has_booking ? 0.85 : 0.5}
                                stroke={only.has_booking ? '#111827' : '#fff'}
                                strokeWidth={0.5 / z}
                                style={{ cursor: 'pointer' }} />
                            </SafeMarker>,
                          ];
                        }
                        const rawR = Math.min(3 + Math.log2(c.count + 1) * 1.3, 10);
                        const r = rawR / z;
                        // Label sits inside the bubble — keep it well below the
                        // bubble diameter so 3-digit cluster counts don't spill.
                        const labelFont = Math.max(5, Math.round(rawR * 0.55)) / z;
                        return [
                          <SafeMarker key={`c-${c.key}`} coordinates={[c.lon, c.lat]}
                            onClick={() => setExpandedClusterKey(c.key)}>
                            <circle r={r}
                              fill={BUCKET_COLORS[c.bucket] || BUCKET_COLORS.unknown}
                              fillOpacity={0.55}
                              stroke="#fff" strokeWidth={0.5 / z}
                              style={{ cursor: 'pointer' }} />
                            <text textAnchor="middle" dy="0.35em" style={{ fontSize: labelFont, fill: '#111827', fontWeight: 800, pointerEvents: 'none' }}>
                              {c.count}
                            </text>
                          </SafeMarker>,
                        ];
                      })
                    ) : (
                      visibleOrigins.map((o, i) => (
                        <SafeMarker key={`m-${i}`} coordinates={[o.origin_lon, o.origin_lat]}
                          onClick={() => updateParam('origin', `${o.origin_lat},${o.origin_lon}`)}>
                          <circle r={(o.has_booking ? 3.2 : 2) / z}
                            fill={BUCKET_COLORS[o.drive_bucket] || BUCKET_COLORS.unknown}
                            fillOpacity={o.has_booking ? 0.85 : 0.5}
                            stroke={o.has_booking ? '#111827' : '#fff'}
                            strokeWidth={0.5 / z}
                            style={{ cursor: 'pointer' }} />
                        </SafeMarker>
                      ))
                    )}

                    {park?.latitude && park?.longitude && (
                      // The property label can land on top of the bubble cluster
                      // when zoomed in (markup #75). A white stroke painted
                      // beneath the fill gives every glyph a readable halo
                      // against any underlying color. The whole group is drawn
                      // last in the layer stack so the marker + label sit on
                      // top of the bubbles, not behind them.
                      <SafeMarker coordinates={[Number(park.longitude), Number(park.latitude)]}>
                        <g style={{ pointerEvents: 'none' }}>
                          <circle r={6 / z} fill="#32B1FF" stroke="#fff" strokeWidth={1.5 / z} />
                          <text
                            y={-12 / z}
                            textAnchor="middle"
                            style={{
                              fontSize: 10 / z,
                              fill: '#111827',
                              stroke: '#fff',
                              strokeWidth: 3 / z,
                              strokeLinejoin: 'round',
                              paintOrder: 'stroke fill',
                              fontWeight: 800,
                            }}
                          >
                            {park.campground_name || 'Property'}
                          </text>
                        </g>
                      </SafeMarker>
                    )}
                  </>
                );
              })()}
            </ZoomableGroup>
          </ComposableMap>

        </div>

        {/* Legend + map controls */}
        <div className="mt-3 flex items-start justify-between gap-3">
          <div className="flex flex-wrap gap-2 min-w-0">
            {BUCKET_ORDER.map((b) => (
              <DriveBucketBadge key={b} bucket={b} active={bucketFilter === b}
                onClick={() => updateParam('bucket', bucketFilter === b ? '' : b)} />
            ))}
          </div>
          <div className="flex flex-col rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm shrink-0">
            <button type="button" onClick={zoomIn} disabled={viewState.zoom >= MAX_ZOOM}
              aria-label="Zoom in"
              className="px-2 py-1 text-base leading-none text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]">
              +
            </button>
            <button type="button" onClick={zoomOut} disabled={viewState.zoom <= MIN_ZOOM}
              aria-label="Zoom out"
              className="px-2 py-1 text-base leading-none text-gray-700 dark:text-gray-200 border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]">
              −
            </button>
            <button type="button" onClick={zoomReset}
              aria-label="Reset zoom"
              title="Reset view"
              className="px-2 py-1 text-xs leading-none text-gray-600 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]">
              ⌂
            </button>
          </div>
        </div>
        {failed.origins && (
          <div className="mt-2">
            <RiError message="Couldn't load guest origins for the map. The request failed or took too long." onRetry={retry} height={60} />
          </div>
        )}
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {origins && origins.length >= ORIGINS_LIMIT && (
            <span className="mr-2 text-amber-600 dark:text-amber-400">
              Showing the first {ORIGINS_LIMIT.toLocaleString()} origins.
            </span>
          )}
          {visibleOrigins.length.toLocaleString()} origins shown {bookedOnly ? '(booked only)' : ''}{showRoutes ? ` · ${decodedPolylines.length.toLocaleString()} routes` : ''}
          {polylineCapHit && (
            <span className="ml-2 text-amber-600 dark:text-amber-400">
              (capped at {MAX_POLYLINES} — filter by bucket or state to narrow)
            </span>
          )}
        </div>
        {nonUsOrigins.length > 0 && (
          <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            Includes {Number(nonUsOrigins.length).toLocaleString()} non-US origin{nonUsOrigins.length === 1 ? '' : 's'} — pan/zoom out to see them.
          </div>
        )}
        {showRoutes && polylineDataAvailable === false && (
          <div className="mt-2 rounded-md border border-amber-300/40 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
            Route geometry isn't available for this dataset yet — origins are shown as bubbles only.
            Highway polylines will appear once the route-intelligence sync stores them.
          </div>
        )}
      </div>

      {/* Ring chart */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Drive Time Distribution</h3>
          {bucketFilter && (
            <button
              type="button"
              onClick={() => updateParam('bucket', '')}
              className="shrink-0 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
              title="Show all drive-time buckets"
            >
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Clear filter
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">How routes split across drive-time buckets. Click a slice to filter the map and state table.</p>
        {failed.distribution ? (
          <RiError onRetry={retry} height={180} />
        ) : ringData.length === 0 ? (
          <RiEmpty message="No route data in this date range" height={180} />
        ) : (
          <>
            {/* Height scales with the viewport; percentage radii let the donut
                grow to fill the (wider) card on large screens and shrink
                cleanly on small ones. */}
            <div className="relative h-56 sm:h-64 lg:h-72 xl:h-80 2xl:h-96">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={ringData} dataKey="value" nameKey="name" innerRadius="56%" outerRadius="84%"
                    onClick={(d) => updateParam('bucket', bucketFilter === d.name ? '' : d.name)}>
                    {ringData.map((d) => (
                      <Cell key={d.name} fill={BUCKET_COLORS[d.name] || BUCKET_COLORS.unknown}
                        fillOpacity={bucketFilter && bucketFilter !== d.name ? 0.3 : 1}
                        style={{ cursor: 'pointer' }} />
                    ))}
                  </Pie>
                  <ThemedChartTooltip
                    formatter={(v, n) => [`${v} routes`, n]}
                    wrapperStyle={{ pointerEvents: 'none' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-6 text-center">
                <div className="text-xl sm:text-2xl xl:text-3xl font-bold text-gray-900 dark:text-white">{ringTotal.toLocaleString()}</div>
                <div className="text-[10px] xl:text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">routes</div>
                {/* When a bucket is filtered the count above is that bucket's
                    total, not the grand total — name it so the number reads
                    clearly. The "Clear filter" chip resets it. */}
                {bucketFilter && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-gray-600 dark:text-gray-300">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: BUCKET_COLORS[bucketFilter] || BUCKET_COLORS.unknown }} />
                    {bucketFilter}
                  </div>
                )}
              </div>
            </div>
            {/* Compact aligned table — the donut already shows the proportions,
                so the list focuses on exact figures (routes / share / fuel) in
                tidy columns rather than repeating a bar. Rows filter the map &
                state table like the donut slices; the active row is highlighted
                and the rest dimmed to match the donut. */}
            <table className="w-full text-xs mt-3">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left font-medium py-1.5">Drive time</th>
                  <th className="text-right font-medium py-1.5">Routes</th>
                  <th className="text-right font-medium py-1.5">Share</th>
                  <th className="text-right font-medium py-1.5">Avg fuel</th>
                </tr>
              </thead>
              <tbody>
                {effectiveDistribution.map((d) => {
                  const pct = Number(d.pct) || 0;
                  const color = BUCKET_COLORS[d.bucket] || BUCKET_COLORS.unknown;
                  const active = bucketFilter === d.bucket;
                  const dimmed = bucketFilter && !active;
                  return (
                    <tr
                      key={d.bucket}
                      onClick={() => updateParam('bucket', active ? '' : d.bucket)}
                      title={active ? 'Clear filter' : `Filter to ${d.bucket}`}
                      className={`cursor-pointer border-b border-gray-100 dark:border-gray-800/60 last:border-0 transition hover:bg-gray-100 dark:hover:bg-gray-800/60 ${active ? 'bg-gray-100 dark:bg-gray-800/60' : ''} ${dimmed ? 'opacity-40' : ''}`}
                    >
                      <td className="py-1.5">
                        <span className="inline-flex items-center gap-2 text-gray-900 dark:text-gray-100">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                          {d.bucket}
                        </span>
                      </td>
                      <td className="py-1.5 text-right tabular-nums font-medium text-gray-900 dark:text-gray-100">{Number(d.routes).toLocaleString()}</td>
                      <td className="py-1.5 text-right tabular-nums text-gray-500 dark:text-gray-400">{pct.toFixed(1)}%</td>
                      <td className="py-1.5 text-right tabular-nums text-gray-500 dark:text-gray-400">~{d.avg_fuel ?? '—'} gal</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* State table */}
      <div className="xl:col-span-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Origins by State</h3>
          {sortedStates.length > 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {sortedStates.length.toLocaleString()} total · page {statePage} / {stateTotalPages}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">State-level rollup of every guest origin. Click a row to drill into the underlying markets and origins.</p>
        {failed.stateTable ? (
          <RiError onRetry={retry} height={120} />
        ) : (!stateTable || stateTable.length === 0) ? (
          <RiEmpty message="Route data is accumulating. State-level analysis will appear once routes from multiple states are computed." height={120} />
        ) : sortedStates.length === 0 ? (
          <RiEmpty message="No states match the current filters." height={120} />
        ) : (
          <>
          <div className={`${RI_TABLE_FRAME} responsive-card-table-wrapper`}>
            <table className="w-full text-sm responsive-card-table">
              <thead className={RI_THEAD}>
                <tr>
                  {[
                    ['state_region', 'State', 'left'],
                    ['total_origins', 'Origins', 'right'],
                    ['avg_drive_hours', 'Avg Drive (hrs)', 'right'],
                    ['avg_miles', 'Avg Miles', 'right'],
                    ['avg_fuel_gallons', 'Avg Fuel (gal)', 'right'],
                    ['avg_congestion_min', 'Avg Congestion (min)', 'right'],
                    ['min_hours', 'Min Drive (hrs)', 'right'],
                    ['max_hours', 'Max Drive (hrs)', 'right'],
                  ].map(([key, label, align]) => {
                    const isSorted = stateSort.key === key;
                    const ariaSort = isSorted
                      ? (stateSort.dir === 'desc' ? 'descending' : 'ascending')
                      : 'none';
                    return (
                      <th
                        key={key}
                        scope="col"
                        aria-sort={ariaSort}
                        className={`${RI_TH} ${align === 'right' ? 'text-right' : 'text-left'} cursor-pointer select-none transition-colors hover:text-gray-900 dark:hover:text-white ${isSorted ? 'text-gray-900 dark:text-white' : ''}`}
                        onClick={() => setStateSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))}
                      >
                        <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
                          <span>{label}</span>
                          <span aria-hidden="true" className={isSorted ? 'text-[#32B1FF]' : 'text-gray-300 dark:text-gray-600'}>
                            {isSorted ? (stateSort.dir === 'desc' ? '↓' : '↑') : '↕'}
                          </span>
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-gray-300">
                {pagedStates.map((r) => {
                  const isActive = stateFilter === r.state_region;
                  const bucketForDrive = r.avg_drive_hours != null
                    ? BUCKET_COLORS[(
                        r.avg_drive_hours <= 1 ? '0-1 hr' :
                        r.avg_drive_hours <= 2 ? '1-2 hrs' :
                        r.avg_drive_hours <= 4 ? '2-4 hrs' :
                        r.avg_drive_hours <= 6 ? '4-6 hrs' :
                        r.avg_drive_hours <= 8 ? '6-8 hrs' :
                        r.avg_drive_hours <= 12 ? '8-12 hrs' : '12+ hrs'
                      )]
                    : null;
                  const originsPct = maxStateOrigins > 0 && r.total_origins != null
                    ? Math.max(4, Math.round((Number(r.total_origins) / maxStateOrigins) * 100))
                    : 0;
                  const congested = r.avg_congestion_min > 30;
                  return (
                    <tr key={r.state_region}
                      {...clickableRowProps(() => updateParam('state', isActive ? '' : r.state_region))}
                      aria-selected={isActive}
                      aria-label={`${r.state_region} — ${isActive ? 'close' : 'open'} state details`}
                      className={`${RI_ROW} ${RI_ROW_CLICKABLE} ${isActive ? RI_ROW_SELECTED : ''}`}>
                      <td className={`${RI_TD} text-gray-900 dark:text-white ${r.total_origins >= 50 ? 'font-bold' : 'font-medium'}`} data-label="State">
                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                          <span>{r.state_region}</span>
                          {r.confidence === 'single_observation' && (
                            <span
                              className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                              title="Single origin observed — emerging marketing signal"
                            >
                              Single
                            </span>
                          )}
                        </span>
                      </td>
                      <td className={`${RI_TD} text-right`} data-label="Origins">
                        <span className="inline-flex items-center justify-end gap-2">
                          <span className="hidden sm:block h-1.5 w-16 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden" aria-hidden="true">
                            <span className="block h-full rounded-full bg-[#32B1FF]" style={{ width: `${originsPct}%` }} />
                          </span>
                          <span className="tabular-nums font-semibold text-gray-900 dark:text-white">{r.total_origins != null ? Number(r.total_origins).toLocaleString() : '—'}</span>
                        </span>
                      </td>
                      <td className={`${RI_TD} text-right`} data-label="Avg Drive (hrs)">
                        {bucketForDrive ? (
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums"
                            style={{ color: bucketForDrive, backgroundColor: `${bucketForDrive}1F` }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: bucketForDrive }} aria-hidden="true" />
                            {fmt1(r.avg_drive_hours)}
                          </span>
                        ) : fmt1(r.avg_drive_hours)}
                      </td>
                      <td className={`${RI_TD} text-right tabular-nums`} data-label="Avg Miles">{r.avg_miles != null ? Math.round(Number(r.avg_miles)).toLocaleString() : '—'}</td>
                      <td className={`${RI_TD} text-right tabular-nums`} data-label="Avg Fuel (gal)">{fmt1(r.avg_fuel_gallons)}</td>
                      <td className={`${RI_TD} text-right tabular-nums`} data-label="Avg Congestion (min)">
                        {congested ? (
                          <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-semibold bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400">{fmt1(r.avg_congestion_min)}</span>
                        ) : fmt1(r.avg_congestion_min)}
                      </td>
                      <td className={`${RI_TD} text-right tabular-nums text-gray-500 dark:text-gray-400`} data-label="Min Drive (hrs)">{fmt1(r.min_hours)}</td>
                      <td className={`${RI_TD} text-right tabular-nums text-gray-500 dark:text-gray-400`} data-label="Max Drive (hrs)">{fmt1(r.max_hours)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <RiSparseNotice count={sortedStates.length} />
            <RiPagination page={statePage} totalPages={stateTotalPages} onPageChange={setStatePage} totalItems={sortedStates.length} itemLabel="state" />
          </div>
          </>
        )}
      </div>

      {stateFilter && (
        <StateDetailPanel
          stateName={stateFilter}
          stateRow={selectedStateRow}
          origins={selectedStateOrigins}
          onClose={() => updateParam('state', '')}
          onSelectOrigin={(o) => updateParam('origin', `${o.origin_lat},${o.origin_lon}`)}
        />
      )}

      {selectedOriginCoords && selectedOriginCoords.length === 2 && !Number.isNaN(selectedOriginCoords[0]) && (
        <OriginDetailPanel
          clientId={clientId}
          originLat={selectedOriginCoords[0]}
          originLon={selectedOriginCoords[1]}
          range={range}
          onClose={() => updateParam('origin', '')}
        />
      )}

      {confirmShow && (
        <ShowRoutesConfirmModal
          count={confirmShow.count}
          max={MAX_POLYLINES}
          onConfirm={() => resolveConfirmShow(true)}
          onCancel={() => resolveConfirmShow(false)}
        />
      )}
    </div>
  );
}

// WCAG 2.1 AA-compliant confirmation modal used before decoding many polylines.
// - role="alertdialog" + aria-modal signals a blocking, action-required surface.
// - aria-labelledby / aria-describedby link the accessible name + description.
// - Initial focus lands on the primary action; Tab is trapped via useFocusTrap;
//   Escape cancels; backdrop click cancels; focus returns to the trigger button
//   (handled by the parent via showRoutesBtnRef).
function ShowRoutesConfirmModal({ count, max, onConfirm, onCancel }) {
  const dialogRef = useRef(null);
  const confirmRef = useRef(null);
  const titleId = 'ri-show-routes-title';
  const descId = 'ri-show-routes-desc';

  useFocusTrap(dialogRef);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      ref={dialogRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        onClick={onCancel}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative w-full max-w-md rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-2xl">
        <div className="px-5 pt-5 pb-3">
          <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-white">
            Show routes for {count.toLocaleString()} origins?
          </h2>
          <div id={descId} className="mt-2 space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <p>
              Only the first <strong className="text-gray-900 dark:text-white">{max.toLocaleString()}</strong> routes in view will be drawn,
              and the map may briefly lag while they decode.
            </p>
            <p className="text-gray-500 dark:text-gray-400">
              Tip: filter by drive-time bucket or state first to focus on fewer routes.
            </p>
          </div>
        </div>
        <div className="px-5 pb-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-md bg-[#32B1FF] text-sm font-medium text-white hover:bg-[#1a9ce6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
